/**
 * JunctionPurposeLabel Component
 *
 * Renders a draggable and resizable text label near a cable junction icon.
 * Shows "FROM: X" or "TO: Y" with a dashed rectangle border.
 * Features:
 * - Draggable to reposition relative to the junction
 * - Resizable width via drag handles
 * - Multi-line text support with auto-wrapping
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import type { CableJunction } from '@/types/cablePaths';

interface JunctionPurposeLabelProps {
  junction: CableJunction;
  scale: number;
  isSelected: boolean;
  isInteractive?: boolean;
  onUpdateLabel?: (updates: {
    label_offset_x?: number;
    label_offset_y?: number;
    label_width?: number;
  }) => void;
}

// Constants for label rendering
const DEFAULT_FONT_SIZE = 10;
const PADDING_X = 8;
const PADDING_Y = 6;
const MIN_WIDTH = 60;
const MAX_WIDTH = 300;
const DEFAULT_OFFSET_X = 25;
const DEFAULT_OFFSET_Y = -15;
const DEFAULT_WIDTH = 120;
const HANDLE_SIZE = 8;
const DRAG_THRESHOLD = 3; // Minimum pixels moved before considering it a drag

// Helper to wrap text into multiple lines based on width
function wrapText(text: string, maxWidth: number, fontSize: number): string[] {
  const charWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor((maxWidth - PADDING_X * 2) / charWidth);

  if (maxCharsPerLine <= 0) return [text];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine =
        word.length > maxCharsPerLine ? word.substring(0, maxCharsPerLine - 2) + '...' : word;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines.length > 0 ? lines : [text];
}

export function JunctionPurposeLabel({
  junction,
  scale,
  isSelected,
  isInteractive = false,
  onUpdateLabel,
}: JunctionPurposeLabelProps) {
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [localOffset, setLocalOffset] = useState<{ x: number; y: number } | null>(null);
  const [localWidth, setLocalWidth] = useState<number | null>(null);

  // Refs for tracking drag/resize state (include current values to avoid closure issues)
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    offsetX: number;
    offsetY: number;
    hasMoved: boolean;
    currentOffsetX: number;
    currentOffsetY: number;
  } | null>(null);
  const resizeStartRef = useRef<{
    mouseX: number;
    width: number;
    side: 'left' | 'right';
    hasMoved: boolean;
    currentWidth: number;
  } | null>(null);

  // Format the label text (compute before early return to satisfy hooks rules)
  const prefix = junction.purpose_type === 'from' ? 'FROM:' : 'TO:';
  const textColor = junction.purpose_type === 'from' ? '#059669' : '#d97706';
  const borderColor = isSelected
    ? '#3b82f6'
    : junction.purpose_type === 'from'
      ? '#059669'
      : '#d97706';

  // Get values from junction or use defaults/local state
  const offsetX = localOffset?.x ?? junction.label_offset_x ?? DEFAULT_OFFSET_X;
  const offsetY = localOffset?.y ?? junction.label_offset_y ?? DEFAULT_OFFSET_Y;
  const labelWidth = localWidth ?? junction.label_width ?? DEFAULT_WIDTH;

  // Font size from junction settings or default
  const fontSize = junction.label_font_size ?? DEFAULT_FONT_SIZE;
  const lineHeight = Math.round(fontSize * 1.4);

  // Position label relative to junction
  const labelX = junction.x + offsetX;
  const labelY = junction.y + offsetY;

  // Wrap text into lines (must be before early return - hooks rules)
  const fullText = `${prefix} ${junction.purpose_label || ''}`;
  const lines = useMemo(
    () => wrapText(fullText, labelWidth, fontSize),
    [fullText, labelWidth, fontSize],
  );

  // Calculate box dimensions
  const textHeight = lines.length * lineHeight;
  const boxHeight = textHeight + PADDING_Y * 2;
  const boxWidth = labelWidth;

  // Handle mouse down to start drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive || !onUpdateLabel || isResizing) return;
      e.stopPropagation();
      e.preventDefault();

      const currentOffsetX = junction.label_offset_x ?? DEFAULT_OFFSET_X;
      const currentOffsetY = junction.label_offset_y ?? DEFAULT_OFFSET_Y;

      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        offsetX: currentOffsetX,
        offsetY: currentOffsetY,
        hasMoved: false,
        currentOffsetX: currentOffsetX,
        currentOffsetY: currentOffsetY,
      };

      setLocalOffset({ x: currentOffsetX, y: currentOffsetY });

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current) return;

        const deltaX = moveEvent.clientX - dragStartRef.current.mouseX;
        const deltaY = moveEvent.clientY - dragStartRef.current.mouseY;

        // Check if we've moved past the threshold
        if (!dragStartRef.current.hasMoved) {
          const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          if (distance < DRAG_THRESHOLD) return;
          dragStartRef.current.hasMoved = true;
          setIsDragging(true);
        }

        const scaledDeltaX = deltaX / scale;
        const scaledDeltaY = deltaY / scale;

        const newOffsetX = dragStartRef.current.offsetX + scaledDeltaX;
        const newOffsetY = dragStartRef.current.offsetY + scaledDeltaY;

        // Store current values in ref to avoid closure issues
        dragStartRef.current.currentOffsetX = newOffsetX;
        dragStartRef.current.currentOffsetY = newOffsetY;

        setLocalOffset({ x: newOffsetX, y: newOffsetY });
      };

      const handleMouseUp = () => {
        // Only save if we actually moved - use values from ref to avoid closure issues
        if (dragStartRef.current?.hasMoved) {
          onUpdateLabel({
            label_offset_x: dragStartRef.current.currentOffsetX,
            label_offset_y: dragStartRef.current.currentOffsetY,
          });
        }

        setIsDragging(false);
        setLocalOffset(null);
        dragStartRef.current = null;

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [
      isInteractive,
      onUpdateLabel,
      isResizing,
      junction.label_offset_x,
      junction.label_offset_y,
      scale,
    ],
  );

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, side: 'left' | 'right') => {
      if (!isInteractive || !onUpdateLabel) return;
      e.stopPropagation();
      e.preventDefault();

      const currentWidth = junction.label_width ?? DEFAULT_WIDTH;

      resizeStartRef.current = {
        mouseX: e.clientX,
        width: currentWidth,
        side,
        hasMoved: false,
        currentWidth: currentWidth,
      };

      setLocalWidth(currentWidth);
      setIsResizing(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!resizeStartRef.current) return;

        const deltaX = moveEvent.clientX - resizeStartRef.current.mouseX;

        // Check if we've moved past the threshold
        if (!resizeStartRef.current.hasMoved) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
          resizeStartRef.current.hasMoved = true;
        }

        const scaledDeltaX = deltaX / scale;
        let newWidth: number;

        if (resizeStartRef.current.side === 'right') {
          newWidth = resizeStartRef.current.width + scaledDeltaX;
        } else {
          newWidth = resizeStartRef.current.width - scaledDeltaX;
        }

        // Clamp width
        newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

        // Store current value in ref to avoid closure issues
        resizeStartRef.current.currentWidth = newWidth;

        setLocalWidth(newWidth);
      };

      const handleMouseUp = () => {
        // Only save if we actually moved - use value from ref to avoid closure issues
        if (resizeStartRef.current?.hasMoved) {
          onUpdateLabel({
            label_width: resizeStartRef.current.currentWidth,
          });
        }

        setIsResizing(false);
        setLocalWidth(null);
        resizeStartRef.current = null;

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [isInteractive, onUpdateLabel, junction.label_width, scale],
  );

  // Don't render if no purpose is set (early return must be after all hooks)
  if (!junction.purpose_type || !junction.purpose_label) {
    return null;
  }

  return (
    <g className="junction-purpose-label" data-purpose-label={junction.id}>
      {/* Connector line from junction to label */}
      <line
        x1={junction.x}
        y1={junction.y}
        x2={labelX + boxWidth / 2}
        y2={labelY + boxHeight / 2}
        stroke={borderColor}
        strokeWidth={1}
        strokeDasharray="4,2"
        opacity={0.5}
        style={{ pointerEvents: 'none' }}
      />

      {/* Main label box with dashed border */}
      <rect
        x={labelX}
        y={labelY}
        width={boxWidth}
        height={boxHeight}
        fill="white"
        fillOpacity={0.95}
        stroke={borderColor}
        strokeWidth={isSelected ? 2 : 1}
        strokeDasharray={isSelected ? '6,3' : '4,2'}
        rx={3}
        ry={3}
        style={{
          cursor: isDragging ? 'grabbing' : isInteractive ? 'grab' : 'default',
          pointerEvents: isInteractive ? 'all' : 'none',
        }}
        onMouseDown={handleMouseDown}
      />

      {/* Text content with multi-line support */}
      <text
        x={labelX + PADDING_X}
        y={labelY + PADDING_Y + fontSize}
        fontSize={fontSize}
        fontFamily="system-ui, -apple-system, sans-serif"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {lines.map((line, index) => {
          // First line has the colored prefix
          if (index === 0) {
            const prefixEnd = line.indexOf(':') + 1;
            const prefixPart = line.substring(0, prefixEnd);
            const restPart = line.substring(prefixEnd);
            return (
              <tspan key={index} x={labelX + PADDING_X} dy={index === 0 ? 0 : lineHeight}>
                <tspan fill={textColor} fontWeight={600}>
                  {prefixPart}
                </tspan>
                <tspan fill="#374151">{restPart}</tspan>
              </tspan>
            );
          }
          return (
            <tspan key={index} x={labelX + PADDING_X} dy={lineHeight} fill="#374151">
              {line}
            </tspan>
          );
        })}
      </text>

      {/* Resize handle - bottom-right corner with diagonal grip lines */}
      {isInteractive && (
        <g
          style={{ cursor: 'nwse-resize', pointerEvents: 'all' }}
          onMouseDown={e => handleResizeStart(e, 'right')}
        >
          {/* Background square */}
          <rect
            x={labelX + boxWidth - HANDLE_SIZE / 2}
            y={labelY + boxHeight - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill={isSelected ? '#3b82f6' : '#9ca3af'}
            stroke="white"
            strokeWidth={1}
            rx={1}
          />
          {/* Diagonal grip lines */}
          <line
            x1={labelX + boxWidth - 1}
            y1={labelY + boxHeight + 3}
            x2={labelX + boxWidth + 3}
            y2={labelY + boxHeight - 1}
            stroke="white"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <line
            x1={labelX + boxWidth + 1}
            y1={labelY + boxHeight + 3}
            x2={labelX + boxWidth + 3}
            y2={labelY + boxHeight + 1}
            stroke="white"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </g>
      )}
    </g>
  );
}
